import { FieldValues, FieldPath, useController } from 'react-hook-form';
import { DayPicker, DateRange, Matcher } from 'react-day-picker';
import {
	FormControl,
	FormHelperText,
	TextField,
	InputAdornment,
	IconButton,
	Popover,
	Box,
	Button,
	useTheme,
	alpha,
} from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import { useState } from 'react';
import dayjs from 'dayjs';
import {
	API_DATE_FORMAT,
	DISPLAY_DATE_FORMAT,
} from '../../../utils/dateHelper';

// 调整Props接口，支持传入两个字段的配置
interface AppDateRangePickerProps<TFieldValues extends FieldValues> {
	label?: string;
	minimumDate?: string;
	maximumDate?: string;
	placeholder?: [string, string];
	disabled?: boolean;
	formatDate?: string;
	handleChange?: (startDate: string | null, endDate: string | null) => void;
	sx?: any;
	variantType?: string;
	control: any; // 表单control
	startDateName: FieldPath<TFieldValues>; // 开始日期字段名
	endDateName: FieldPath<TFieldValues>; // 结束日期字段名
	startDateRules?: any; // 开始日期验证规则
	endDateRules?: any; // 结束日期验证规则
}

export default function AppDateRangePicker<
	TFieldValues extends FieldValues = FieldValues,
>({
	label,
	minimumDate,
	maximumDate,
	placeholder = ['YYYY-MM-DD', 'YYYY-MM-DD'],
	disabled,
	handleChange,
	variantType,
	sx,
	control,
	startDateName,
	endDateName,
	startDateRules,
	endDateRules,
	formatDate,
}: AppDateRangePickerProps<TFieldValues>) {
	const theme = useTheme();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	// 分别控制开始/结束日期两个字段
	const { field: startField, fieldState: startFieldState } = useController({
		name: startDateName,
		control,
		rules: startDateRules,
	});

	const { field: endField, fieldState: endFieldState } = useController({
		name: endDateName,
		control,
		rules: endDateRules,
	});

	// 合并错误信息（优先显示开始日期错误）
	const errorMessage =
		startFieldState.error?.message || endFieldState.error?.message;

	const fmt = formatDate || API_DATE_FORMAT;

	// 解析字符串为Date对象（react-day-picker 使用原生 Date）
	const parseToDate = (
		dateStr: string | undefined | null
	): Date | undefined => {
		if (!dateStr || dateStr === '') return undefined;
		const parsed = dayjs(dateStr, fmt);
		return parsed.isValid() ? parsed.toDate() : undefined;
	};

	// 格式化Date为存储用字符串
	const formatToValue = (date: Date | undefined): string | null => {
		return date ? dayjs(date).format(fmt) : null;
	};

	// 格式化为显示用字符串
	const toDisplay = (dateStr: string | undefined | null): string => {
		if (!dateStr || dateStr === '') return '';
		const parsed = dayjs(dateStr, fmt);
		return parsed.isValid() ? parsed.format(DISPLAY_DATE_FORMAT) : '';
	};

	// 组合两个字段的值为日期范围
	const selectedRange: DateRange | undefined = parseToDate(startField.value)
		? { from: parseToDate(startField.value), to: parseToDate(endField.value) }
		: undefined;

	// 输入框显示的文字
	const startDisplay = toDisplay(startField.value);
	const endDisplay = toDisplay(endField.value);
	const displayValue =
		startDisplay || endDisplay ? `${startDisplay} – ${endDisplay}` : '';

	// min / max 限制（映射为 react-day-picker 的 disabled matcher）
	const disabledMatchers: Matcher[] = [];
	const minDate = parseToDate(minimumDate);
	const maxDate = parseToDate(maximumDate);
	if (minDate) disabledMatchers.push({ before: minDate });
	if (maxDate) disabledMatchers.push({ after: maxDate });

	const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
		if (disabled) return;
		setAnchorEl(event.currentTarget);
	};

	const handleSelect = (range: DateRange | undefined) => {
		const startStr = formatToValue(range?.from);
		const endStr = formatToValue(range?.to);

		// 分别更新两个独立字段
		startField.onChange(startStr);
		endField.onChange(endStr);

		// 触发自定义回调
		if (handleChange) {
			handleChange(startStr, endStr);
		}

		// 选定完整区间后自动关闭
		if (range?.from && range?.to) {
			setAnchorEl(null);
		}
	};

	const handleClear = () => {
		startField.onChange(null);
		endField.onChange(null);
		if (handleChange) {
			handleChange(null, null);
		}
	};

	const OpenIcon = endFieldState.error
		? EventBusyOutlinedIcon
		: CalendarTodayOutlinedIcon;

	return (
		<FormControl fullWidth error={!!errorMessage} sx={sx}>
			<TextField
				variant={(variantType as any) ?? 'outlined'}
				fullWidth
				disabled={disabled}
				label={label}
				value={displayValue}
				error={!!errorMessage}
				// 聚焦样式由 day picker 是否打开来控制（而非原生 DOM focus），
				// 打开时显示聚焦边框，关闭时不显示
				focused={Boolean(anchorEl)}
				onClick={handleOpen}
				inputProps={{
					readOnly: true,
					placeholder: placeholder
						? `${placeholder[0]} - ${placeholder[1]}`
						: undefined,
					style: { cursor: disabled ? 'default' : 'pointer' },
				}}
				InputProps={{
					endAdornment: (
						<InputAdornment position='end'>
							<IconButton
								edge='end'
								disabled={disabled}
								onClick={handleOpen}
								size='medium'
							>
								<OpenIcon fontSize='medium' />
							</IconButton>
						</InputAdornment>
					),
				}}
			/>
			<FormHelperText>{errorMessage}</FormHelperText>

			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
			>
				<Box
					sx={{
						px: '16px',
						py: '16px',
						// react-day-picker 把这些变量定义在 .rdp-root 自身，
						// 所以必须直接覆盖在 .rdp-root 上，写在祖先元素无效
						'& .rdp-root': {
							'--rdp-accent-color': theme.palette.primary.main,
							'--rdp-accent-background-color': theme.palette.primary.main,
							'--rdp-range_start-date-background-color':
								theme.palette.primary.main,
							'--rdp-range_end-date-background-color':
								theme.palette.primary.main,
							'--rdp-range_start-color': theme.palette.primary.contrastText,
							'--rdp-range_end-color': theme.palette.primary.contrastText,
							'--rdp-range_middle-background-color': alpha(
								theme.palette.primary.main,
								0.12
							),
							'--rdp-range_middle-color': theme.palette.text.primary,
							'--rdp-today-color': theme.palette.primary.main,
						},
					}}
				>
					<DayPicker
						mode='range'
						numberOfMonths={2}
						selected={selectedRange}
						onSelect={handleSelect}
						disabled={
							disabledMatchers.length > 0 ? disabledMatchers : undefined
						}
						defaultMonth={selectedRange?.from}
						resetOnSelect
					/>
					<Box
						sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}
					>
						<Button
							size='small'
							onClick={handleClear}
							sx={{ textTransform: 'none' }}
						>
							Clear
						</Button>
						<Button
							size='small'
							variant='contained'
							onClick={() => setAnchorEl(null)}
							sx={{ textTransform: 'none' }}
						>
							Done
						</Button>
					</Box>
				</Box>
			</Popover>
		</FormControl>
	);
}
